import React, {useEffect, useRef, useState} from 'react';
import {ReactComponent as PlusIcon} from '../../../../../../assets/main/groups/main-table/period-calendar/plus.svg';
import {Link, useParams} from "react-router-dom";
import {useAppDispatch, useAppSelector} from "common/hook";
import {ROLE_DEMO_STUDENT, ROLE_HR, ROLE_TEACHER} from "../../../../../../entities/constants/constants";
import {fetchLessonsByGroupsId} from "features/model/service/groups/groups-lessons/fetchLessonsByGroupsId";
import {fetchLessonsDemo} from "features/model/service/lessons/lessons-demo/fetchLessonsDemo";
import {CompanySummaryTable} from "features/model/types/hr/getSummaryTable";
import {getSummaryTable} from "features/model/service/hr/get-summary-table/getSummaryTable";
import {getSummaryTableExcel} from "features/model/service/hr/get-summary-table-excel/getSummaryTableExcel";
import {setSummaryTableFilters} from "features/model/slice/hr/summary-table/summaryTableSlice";
import {ShowEndedGroupsCheckbox} from "components/pages/hr/show-ended-groups-checkbox/ShowEndedGroupsCheckbox";
import {DatePicker} from "antd";


interface IPeriodCalendar {
    create: boolean
    print: boolean
    company?: CompanySummaryTable
    setCurrentFrom?: (value: string) => void;
    setCurrentTo?: (value: string) => void;
    value?: number;
    page?: number;
}

export const PeriodCalendar = (
    {
        create,
        print,
        company,
        setCurrentFrom,
        setCurrentTo,
        value,
        page
    }: IPeriodCalendar) => {
    const roleType = useAppSelector((state) => state.role.roleType);
    const includeEnded = useAppSelector((state) => state.summaryTable.filters.includeEnded);
    const dispatch = useAppDispatch();
    const [isOpenWith, setIsOpenWith] = useState(false);
    const [isOpenTo, setIsOpenTo] = useState(false);
    const fromNew = useAppSelector((state) => state.date.dateWithNew);
    const toNew = useAppSelector((state) => state.date.dateToNew);
    const groupId = useParams().groupId!;
    const Loading = useAppSelector((state) => state.groupsLessons.IsLoading);
    const [isLoading, setIsLoading] = useState(Loading);

    const calendarRef = useRef<HTMLDivElement>(null);
    const {RangePicker} = DatePicker;

    const [from, setFrom] = useState<string>('');
    const [to, setTo] = useState<string>('');
    const isHrSummary = roleType === ROLE_HR && !window.location.pathname.includes('/groups/');


    const handleClickOutside = (event: MouseEvent) => {
        if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
            handleClose();
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const fetchData = (dispatchFn: any, params: any) => {
        setIsLoading(true);

        dispatchFn(params)
            .then(() => {
                setIsLoading(false);
            })
            .catch((error: string) => {
                console.error('Error fetching lessons:', error);
                setIsLoading(false);
            });
    };

    // HR summary: dates / includeEnded (pagination goes through Table + Redux filters)
    useEffect(() => {
        if (isHrSummary) {
            fetchData(dispatch, getSummaryTable({from, to, page: 0, includeEnded}));
        }
    }, [dispatch, isHrSummary, from, to, includeEnded]);

    useEffect(() => {
        if (isHrSummary) {
            return;
        }
        if (roleType === ROLE_DEMO_STUDENT) {
            fetchData(dispatch, fetchLessonsDemo({from, to, page}));
        } else {
            fetchData(dispatch, fetchLessonsByGroupsId({groupId, from, to, page}));
            setCurrentFrom?.(from);
            setCurrentTo?.(to);
        }
    }, [dispatch, isHrSummary, roleType, groupId, from, to, page]);


    const togglePopupWith = () => {
        setIsOpenWith(!isOpenWith);
        setIsOpenTo(false);
    };

    const togglePopupTo = () => {
        setIsOpenTo(!isOpenTo);
        setIsOpenWith(false);
    };

    const handleClose = () => {
        setIsOpenWith(false);
        setIsOpenTo(false);
    };

    const downloadExcelHandler = () => {
        dispatch(getSummaryTableExcel({from, to, includeEnded}));
    };

    const onIncludeEndedChange = (checked: boolean) => {
        dispatch(setSummaryTableFilters({includeEnded: checked}));
    };

    return (
        <div className="tabs-content">
            {print && (
                <div className="data-tbl-title-box">
                    <div className="title-col">
                        <p className="h3">{company?.title}</p>
                    </div>

                    <div className="sub-col">
                        <span className="btn secondary small print-action" onClick={downloadExcelHandler}>
                            <span>Распечатать отчёт</span>
                        </span>
                    </div>
                </div>
            )}
            <div className="tab">
                <div className="data-tbl-controls controls_data-tbl">
                    <div className='controls_data-tbl-block'>
                        <RangePicker
                            showTime={false}
                            format="DD MMM YYYY"
                            onChange={(value) => {
                                if (value && value[0] && value[1]) {
                                    const formattedFrom = value[0].format('DD/MM/YYYY');
                                    const formattedTo = value[1].format('DD/MM/YYYY');
                                    setFrom(formattedFrom);
                                    setTo(formattedTo);
                                } else {
                                    setFrom('');
                                    setTo('');
                                }
                            }}
                        />
                        {
                            isLoading && <span className='loader abs1'></span>
                        }
                        {isHrSummary && (
                            <ShowEndedGroupsCheckbox
                                checked={includeEnded}
                                onChange={onIncludeEndedChange}
                            />
                        )}
                    </div>

                    {roleType === ROLE_TEACHER && (
                        <>
                            {create && (
                                <Link to={`/groups/${groupId}/add-lesson`} className="btn small has-icon">
                                    <span className="icon">
                                        <PlusIcon/>
                                    </span>
                                    <span>Создать занятие</span>
                                </Link>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

